/**
 * Chest API Client
 *
 * Handles chest opening with backend cosigning.
 * Flow:
 * 1. Create tx orders (backend splits into batches of max 30 chests)
 * 2. Get pre-signed transactions from backend
 * 3. User signs and submits to Solana
 */

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

export interface CreateOrdersRequest {
  amount: number; // Number of chests to open
}

export interface CreateOrdersResponse {
  code: number;
  data: string[]; // Array of order IDs
  message?: string;
}

export interface GenerateSignaturesRequest {
  order_ids: string[];
}

export interface GenerateSignaturesResponse {
  code: number;
  data: {
    signatures: Array<{
      order_id: string;
      serialized_tx: string; // Hex-encoded partially signed transaction
    }>;
  };
  message?: string;
}

export interface TxOrderDetail {
  order_id: string;
  user_id: number;
  address: string;
  type: number;
  sub_type: number;
  status: number;
  metadata: {
    index: number;
    total: number;
    amount: number;
    chest_count?: number;
    reward_amounts?: number[];
    reward_indexes?: number[];
    isPoints?: boolean[];
  };
  create_time: string;
  update_time: string;
}

export interface GetOrderDetailResponse {
  code: number;
  data: TxOrderDetail;
  message?: string;
}

/**
 * Create chest opening orders
 * Backend will split into batches of max 30 chests each
 * Uses JWT token from HTTP-only cookie
 *
 * @param amount - Number of chests to open
 * @returns Array of order IDs
 */
export async function createChestOrders(
  amount: number
): Promise<CreateOrdersResponse> {
  const response = await fetch(`${BASE_URL}/chest/create-orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ amount }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create orders: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get pre-signed transactions from backend
 * Backend signs with its authority keypair
 * Uses JWT token from HTTP-only cookie
 *
 * @param orderIds - Array of order IDs
 * @returns Array of serialized transactions (hex encoded)
 */
export async function generateSignatures(
  orderIds: string[]
): Promise<GenerateSignaturesResponse> {
  const response = await fetch(`${BASE_URL}/tx-order/generate-signatures`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ order_ids: orderIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate signatures: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get transaction order details
 * Uses JWT token from HTTP-only cookie
 *
 * @param orderId - Order ID
 * @returns Order details
 */
export async function getOrderDetail(
  orderId: string
): Promise<GetOrderDetailResponse> {
  const response = await fetch(`${BASE_URL}/tx-order/detail?order_id=${orderId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get order detail: ${response.statusText}`);
  }

  return response.json();
}

export interface ParseTransactionRewardsResponse {
  code: number;
  data: {
    chestsOpened: number;
    rewardIndexes: number[];
    rewardAmounts: number[];
    totalPayout: number;
    totalPayoutSol: number;
    rewards: Array<{
      tierIndex: number;
      amount: number;
      amountSol: number;
    }>;
    tierPointsEarned: number;
    pointRewards: Array<{
      tierIndex: number;
      amount: number;
    }>;
    processing?: boolean; // Indicates transaction is still being processed
  };
  message?: string;
}

/**
 * Parse transaction rewards from a confirmed transaction
 * Uses JWT token from HTTP-only cookie
 *
 * @param signature - Transaction signature (optional if order_id provided)
 * @param order_id - Order ID (preferred method)
 * @returns Parsed rewards
 */
export async function parseTransactionRewards(
  signature?: string,
  order_id?: string
): Promise<ParseTransactionRewardsResponse> {
  const response = await fetch(`${BASE_URL}/tx-order/parse-transaction-rewards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ signature, order_id }),
  });

  if (!response.ok) {
    throw new Error(`Failed to parse transaction rewards: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll order details until it's confirmed with rewards
 * Backend sync service runs every 10 seconds, so we need to poll
 * Uses JWT token from HTTP-only cookie
 *
 * @param orderId - Order ID to poll
 * @param maxAttempts - Maximum number of polling attempts (default 20)
 * @param delayMs - Delay between attempts in milliseconds (default 1000)
 * @returns Order detail with reward metadata
 */
export async function pollOrderUntilConfirmed(
  orderId: string,
  maxAttempts: number = 20,
  delayMs: number = 1000,
  onAttempt?: (attempt: number) => void
): Promise<TxOrderDetail> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    onAttempt?.(attempt);
    try {
      const response = await getOrderDetail(orderId);

      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || 'Failed to get order detail');
      }

      const order = response.data;

      // DEBUG: Log every poll attempt with full order details
      console.log(`🔍 [pollOrderUntilConfirmed] Attempt ${attempt}/${maxAttempts} for order ${orderId}:`, {
        status: order.status,
        statusType: typeof order.status,
        metadata: order.metadata,
        hasChestCount: order.metadata?.chest_count !== undefined,
        chestCount: order.metadata?.chest_count,
        rewardAmounts: order.metadata?.reward_amounts,
        fullOrder: JSON.stringify(order, null, 2),
      });

      // Check if order has been confirmed with chest data
      // Status 3 = SUCCESS - backend updates status and metadata atomically in a single transaction
      // Note: reward_amounts can be empty if user didn't win anything (legitimate outcome)
      // The backend still credits base points for chest opens regardless of on-chain rewards
      if (order.status === 3 &&
          order.metadata?.chest_count !== undefined) {
        console.log(`✅ [pollOrderUntilConfirmed] Order ${orderId} confirmed on attempt ${attempt}:`, {
          status: order.status,
          chest_count: order.metadata.chest_count,
          reward_amounts: order.metadata.reward_amounts,
          isPoints: order.metadata.isPoints,
          reward_indexes: order.metadata.reward_indexes,
        });

        return order;
      }

      // Order is still being processed
      if (attempt < maxAttempts) {
        const hasPartialData = order.metadata?.chest_count !== undefined;
        console.log(`⏳ [pollOrderUntilConfirmed] Order ${orderId} not ready yet (attempt ${attempt}/${maxAttempts}, status: ${order.status}, hasPartialData: ${hasPartialData}), retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    } catch (error) {
      console.warn(`⚠️ [pollOrderUntilConfirmed] Error fetching order ${orderId} (attempt ${attempt}/${maxAttempts}):`, error);

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      } else {
        throw error;
      }
    }
  }

  throw new Error(`Order ${orderId} not confirmed after ${maxAttempts} attempts (${maxAttempts * delayMs / 1000}s)`);
}
